import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { withLang } from "./SeoHead";
import { useLang } from "../contexts/LangContext";
import api from "../lib/api";
import Logo from "./Logo";
import { Mail, Phone, MapPin, Facebook, Instagram, Youtube, X, FileText } from "lucide-react";

const LEGAL_KEYS = ["statuts", "roi", "privacy", "gdpr"];

export default function Footer() {
  const { t, lang } = useLang();
  const year = new Date().getFullYear();
  const [s, setS] = useState({});
  const [legals, setLegals] = useState([]);
  const [popup, setPopup] = useState(null);

  useEffect(() => {
    api.get("/site-settings").then((r) => setS(r.data || {})).catch(() => {});
    api.get("/legal-pages").then((r) => setLegals(r.data || [])).catch(() => {});
  }, []);

  const tagline   = s[`tagline_${lang}`] || s.tagline_fr || t.footer.tagline;
  const address   = s[`address_${lang}`] || s.address_fr || t.contact.addressValue;
  const phone     = s.phone   || "+32 4 264 00 00";
  const email     = s.email   || "info@rycm.be";

  const findLegal = (slug) => legals.find((l) => l.slug === slug);

  return (
    <footer className="bg-navy text-white relative" data-testid="main-footer">
      <div className="h-1 bg-gradient-to-r from-club-red via-club-yellow to-club-red" />

      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <Logo size="lg" invert />
          <p className="text-slate-300 text-sm leading-relaxed max-w-md mt-6" data-testid="footer-tagline">{tagline}</p>
          <div className="flex items-center gap-4 mt-6">
            {s.facebook_url !== "" && (
              <a href={s.facebook_url || "https://facebook.com"} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-club-yellow transition-colors" data-testid="social-facebook">
                <Facebook className="w-5 h-5" strokeWidth={1.5} />
              </a>
            )}
            {s.instagram_url !== "" && (
              <a href={s.instagram_url || "https://instagram.com"} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-club-yellow transition-colors" data-testid="social-instagram">
                <Instagram className="w-5 h-5" strokeWidth={1.5} />
              </a>
            )}
            {s.youtube_url !== "" && (
              <a href={s.youtube_url || "https://youtube.com"} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-club-yellow transition-colors" data-testid="social-youtube">
                <Youtube className="w-5 h-5" strokeWidth={1.5} />
              </a>
            )}
          </div>
        </div>

        <div className="md:col-span-4">
          <div className="label-eyebrow mb-4 text-club-yellow">{t.nav.contact}</div>
          <ul className="space-y-3 text-sm text-slate-300">
            <li className="flex items-start gap-2" data-testid="footer-address">
              <MapPin className="w-4 h-4 text-club-yellow mt-0.5 shrink-0" strokeWidth={1.5} />
              <span>{address}</span>
            </li>
            <li className="flex items-start gap-2" data-testid="footer-phone">
              <Phone className="w-4 h-4 text-club-yellow mt-0.5 shrink-0" strokeWidth={1.5} />
              <span>{phone}</span>
            </li>
            <li className="flex items-start gap-2" data-testid="footer-email">
              <Mail className="w-4 h-4 text-club-yellow mt-0.5 shrink-0" strokeWidth={1.5} />
              <span>{email}</span>
            </li>
            {s.iban && (
              <li className="text-xs text-slate-400 mt-3 pt-3 border-t border-white/10" data-testid="footer-iban">
                IBAN&nbsp;: <span className="font-mono">{s.iban}</span>
                {s.bic && <> · BIC&nbsp;: <span className="font-mono">{s.bic}</span></>}
              </li>
            )}
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="label-eyebrow mb-4 text-club-yellow">Navigation</div>
          <ul className="space-y-2 text-sm text-slate-300">
            <li><Link to={withLang(lang, "/club")} className="hover:text-club-yellow transition-colors">{t.nav.club}</Link></li>
            <li><Link to={withLang(lang, "/voile")} className="hover:text-club-yellow transition-colors">{t.nav.voile}</Link></li>
            <li><Link to={withLang(lang, "/regattas")} className="hover:text-club-yellow transition-colors">{t.nav.regattas}</Link></li>
            <li><Link to={withLang(lang, "/stages")} className="hover:text-club-yellow transition-colors">{t.nav.stages}</Link></li>
            <li><Link to={withLang(lang, "/shop")} className="hover:text-club-yellow transition-colors">{t.nav.shop}</Link></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-6 flex flex-col gap-4 text-xs text-slate-400">
          <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-6 gap-y-2" data-testid="footer-legal-links">
            {LEGAL_KEYS.map((slug) => {
              const lp = findLegal(slug);
              if (!lp) return null;
              const label = lp[`title_${lang}`] || lp.title_fr || lp.title_en || lp.title_nl;
              const hasContent = lp[`content_${lang}`] || lp.content_fr || lp.content_en || lp.content_nl;
              if (!label || !hasContent) return null;
              return (
                <button
                  key={slug}
                  onClick={() => setPopup(lp)}
                  className="text-slate-400 hover:text-club-yellow uppercase tracking-widest text-[10px] flex items-center gap-1"
                  data-testid={`footer-legal-${slug}`}
                >
                  <FileText className="w-3 h-3" /> {label}
                </button>
              );
            })}
          </div>
          <div className="flex flex-col md:flex-row items-center justify-between gap-2">
            <div>© {year} Royal Yacht Club de la Meuse — {t.footer.rights}</div>
          </div>
        </div>
      </div>

      {popup && <LegalPopup page={popup} onClose={() => setPopup(null)} lang={lang} />}
    </footer>
  );
}

function LegalPopup({ page, onClose, lang }) {
  const title   = page[`title_${lang}`]   || page.title_fr || page.title_en || page.title_nl;
  const content = page[`content_${lang}`] || page.content_fr || page.content_en || page.content_nl;
  const files = page.files || {};
  const file = files[lang] || files.fr || files.en || files.nl;
  const chrome = {
    fr: { eyebrow: "Document légal", updated: "Dernière mise à jour", download: "Télécharger le document" },
    nl: { eyebrow: "Juridisch document", updated: "Laatst bijgewerkt", download: "Document downloaden" },
    en: { eyebrow: "Legal document", updated: "Last updated", download: "Download the document" },
  }[lang] || { eyebrow: "Document légal", updated: "Dernière mise à jour", download: "Télécharger le document" };
  return (
    <div className="fixed inset-0 z-[60] bg-navy/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="legal-popup" onClick={onClose}>
      <div className="bg-white text-navy max-w-3xl w-full relative flex flex-col max-h-[88vh]" onClick={(e) => e.stopPropagation()}>
        <div className="p-8 md:p-10 pb-4 md:pb-4 shrink-0 border-b border-slate-100 relative">
          <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-navy" data-testid="legal-popup-close">
            <X className="w-5 h-5" />
          </button>
          <div className="label-eyebrow text-club-red mb-2">{chrome.eyebrow}</div>
          <h2 className="font-serif text-2xl md:text-3xl text-navy pr-8" data-testid="legal-popup-title">{title}</h2>
        </div>
        <div className="overflow-y-auto p-8 md:p-10 pt-6 md:pt-6">
        <div
          className="max-w-none text-slate-700 text-sm leading-relaxed break-words
            [&_h1]:font-serif [&_h1]:text-2xl [&_h1]:text-navy [&_h1]:mt-6 [&_h1]:mb-3
            [&_h2]:font-serif [&_h2]:text-xl [&_h2]:text-navy [&_h2]:mt-6 [&_h2]:mb-2
            [&_h3]:font-serif [&_h3]:text-lg [&_h3]:text-navy [&_h3]:mt-5 [&_h3]:mb-2
            [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3
            [&_li]:mb-1 [&_a]:text-club-red [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-navy
            [&_strong]:text-navy [&_table]:w-full [&_table]:text-left [&_td]:py-1 [&_td]:pr-3 [&_th]:py-1 [&_th]:pr-3"
          data-testid="legal-popup-content"
          dangerouslySetInnerHTML={{ __html: /<\/?[a-z][\s\S]*>/i.test(content || "") ? content : (content || "").replace(/\n/g, "<br/>") }}
        />
        {file && (
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api${file.url}`}
            target="_blank" rel="noopener noreferrer" download
            className="inline-flex items-center gap-2 mt-6 bg-club-red text-white px-5 py-3 text-xs uppercase tracking-[0.2em] hover:bg-navy transition-colors"
            data-testid="legal-popup-download"
          >
            <FileText className="w-4 h-4" /> {chrome.download} ({(file.ext || "pdf").toUpperCase()})
          </a>
        )}
        {page.updated_at && (
          <div className="text-xs text-slate-400 mt-8 pt-4 border-t border-slate-100">
            {chrome.updated}&nbsp;: {new Date(page.updated_at).toLocaleDateString(lang === "en" ? "en-GB" : lang)}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
